import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FIX_FIELD_PARAM } from '../utils/focusFixField';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Alert,
  AlertDescription,
  Badge,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import { AlertTriangle, ChevronDown, Search } from 'lucide-react';
import {
  getAgencyClinic,
  getAgencyClinics,
  getAgencyEnrollmentCount,
  type AgencyClinicResponse,
} from '../services/clinicsApi';
import PhysiciansSection from './Admin/PhysiciansSection';
import InternalReferralSourcesSection from './Admin/InternalReferralSourcesSection';
import SchedulingPointsSection from './Admin/SchedulingPointsSection';
import HhrgCostsSection from './Admin/HhrgCostsSection';
import VisitTypesSection from './Admin/visit-types/VisitTypesSection';
import UserLicenseTypesSection from './Admin/UserLicenseTypesSection';
import FormTemplatesSection from './Admin/FormTemplatesSection';
import CahpsSection from './Admin/CahpsSection';
import ClinicInfoSection from './Admin/ClinicInfoSection';
import InsuranceSection from './Admin/InsuranceSection';
import PatientTagsAdminSection from './Admin/PatientTagsAdminSection';
import FaxNumberPortingSection from './Admin/FaxNumberPortingSection';
import {
  AgencyPrintNameSection,
  ChartWorkflowSection,
  ExternalEmrSection,
  OnboardingDatesSection,
  PayrollSettingsSection,
} from '../components/admin';
import { PermissionSlug } from '../constants/featureFlags';
import {
  ADMIN_SECTION_GROUP_LABELS,
  adminNavEntries,
  visibleAdminSections,
  type AdminSectionDefinition,
} from '../constants/adminSections';
import { useBreadcrumbs } from '../components/breadcrumbs/BreadcrumbContext';

const Admin: React.FC = () => {
  const {
    user,
    showError,
    hasAnyPermission,
    hasPermission,
    hasFeatureFlag,
    fetchAgencyClinics,
  } = useStore(
    useShallow(state => ({
      user: state.user,
      showError: state.showError,
      hasAnyPermission: state.hasAnyPermission,
      hasPermission: state.hasPermission,
      hasFeatureFlag: state.hasFeatureFlag,
      fetchAgencyClinics: state.fetchAgencyClinics,
    }))
  );

  const isSuperUser = hasPermission(PermissionSlug.ADMIN_SUPER_USER);

  const [physiciansRefreshToken, setPhysiciansRefreshToken] = useState(0);
  const [
    internalReferralSourcesRefreshToken,
    setInternalReferralSourcesRefreshToken,
  ] = useState(0);
  const [insurancesRefreshToken, setInsurancesRefreshToken] = useState(0);

  // Clinics list — shared across Admin sections (physicians, insurance, etc.).
  const [clinics, setClinics] = useState<AgencyClinicResponse[]>([]);
  const [isLoadingClinics, setIsLoadingClinics] = useState(false);

  const isKinnserEnabled = user?.agency?.is_kinnser_enabled ?? false;
  const isRogerEmrEnabled = user?.agency?.is_rogeremr_enabled ?? false;

  const [searchParams, setSearchParams] = useSearchParams();

  // A section shows when the user can see it at all. `editSlug` counts on its
  // own: an edit-only grant still has to render the thing it edits.
  //
  // Not memoized: `hasAnyPermission` is a stable store function that reads the
  // permission list at call time, so a memo keyed on it would never recompute
  // and the tab strip would keep the old answer after a mid-session grant or
  // revoke, while the section-level useAdminSectionAccess calls already show
  // the new one. It filters eight entries.
  const visibleSections = visibleAdminSections(
    hasAnyPermission,
    isRogerEmrEnabled,
    hasFeatureFlag,
    user?.is_roger_employee === true
  );

  // Patient Creation is now a column of Visit Types; old links land there.
  const requestedSection =
    searchParams.get('section') === 'patient-creation'
      ? 'task-types'
      : searchParams.get('section');
  const activeSectionDefinition =
    visibleSections.find(section => section.id === requestedSection) ??
    visibleSections[0];
  const activeSection = activeSectionDefinition?.id ?? '';
  useBreadcrumbs(
    activeSectionDefinition ? [{ label: activeSectionDefinition.label }] : []
  );

  // Fetched here rather than inside the tab: the tab only mounts when it is
  // selected, and the whole point of the count is to be readable without
  // opening it. Its own endpoint answers with a number instead of every row.
  const showsEnrollments = visibleSections.some(
    section => section.id === 'insurance'
  );
  const [enrollmentCount, setEnrollmentCount] = useState<number | null>(null);
  // Read, not depended on: the effect asks once, and switching tabs is not a
  // reason to ask again. With `activeSection` in the dependencies every hop
  // around Admin re-ran a query that loads every clinic, record and payer to
  // return one integer.
  const activeSectionRef = useRef(activeSection);
  activeSectionRef.current = activeSection;
  useEffect(() => {
    if (!showsEnrollments) return;
    // The mounted tab reports its own count, and both answers write the same
    // state — whichever landed second would win. So when the tab is already
    // open, let it be the one source. Checked again when the answer lands: the
    // tab may have been opened while this was in flight.
    if (activeSectionRef.current === 'insurance') return;
    let cancelled = false;
    void getAgencyEnrollmentCount()
      .then(count => {
        if (!cancelled && activeSectionRef.current !== 'insurance') {
          setEnrollmentCount(count);
        }
      })
      // Swallowed on purpose: a count that will not load is a missing badge,
      // not something to interrupt the Admin page for. The tab itself reports
      // its own failures.
      .catch(error => console.error('Failed to load enrollment count', error));
    return () => {
      cancelled = true;
    };
  }, [showsEnrollments]);

  // A link from another screen (the NOA form's billing contact) can ask for one
  // clinic's edit dialog. The request is dropped from the URL once the Clinics
  // section has acted on it, so a reload or closing the dialog does not reopen it.
  const requestedEditClinicId = searchParams.get('editClinic');
  const requestedClinicField = searchParams.get(FIX_FIELD_PARAM);
  const clearSearchParams = (...keys: string[]) => {
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous);
        keys.forEach(key => next.delete(key));
        return next;
      },
      { replace: true }
    );
  };
  const clearEditClinicRequest = () =>
    clearSearchParams('editClinic', FIX_FIELD_PARAM);

  // Create Claim's missing-payer error links here to open one insurance's edit
  // window, dropped from the URL once acted on, like `editClinic`.
  const requestedEditInsuranceId = searchParams.get('editInsurance');
  const clearEditInsuranceRequest = () => clearSearchParams('editInsurance');

  // Finds a section by its label, its group's label, or its keywords.
  const [navQuery, setNavQuery] = useState('');

  const handleSectionChange = (sectionId: string) => {
    setNavQuery('');
    // `replace` so tabbing around does not fill the back button with each stop.
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous);
        next.set('section', sectionId);
        return next;
      },
      { replace: true }
    );
  };

  const query = navQuery.trim().toLowerCase();
  const navEntries = adminNavEntries(visibleSections);
  const navResults = query
    ? visibleSections.filter(section =>
        [
          section.label,
          ADMIN_SECTION_GROUP_LABELS[section.group],
          ...(section.keywords ?? []),
        ].some(word => word?.toLowerCase().includes(query))
      )
    : [];

  const loadClinics = async () => {
    if (!user?.agency?.id) return;

    setIsLoadingClinics(true);
    try {
      const data = await getAgencyClinics(user.agency.id);
      // The list is one page (default limit 100), so a clinic an NOA links to
      // can fall outside it. Fetch that clinic on its own so the edit dialog
      // still opens; if it cannot be read, the plain list is the fallback.
      if (
        requestedEditClinicId &&
        !data.some(clinic => clinic.id === requestedEditClinicId)
      ) {
        try {
          data.push(await getAgencyClinic(requestedEditClinicId));
        } catch (error) {
          console.error('Failed to load requested clinic', error);
        }
      }
      setClinics(data);
    } catch (error) {
      console.error('Failed to load clinics:', error);
      showError('Failed to load clinic data');
    } finally {
      setIsLoadingClinics(false);
    }
  };

  /** Refresh Admin list and global store so intake / other routes see clinic edits (store caches ~5m). */
  const loadClinicsAndSyncStore = async () => {
    await loadClinics();
    if (user?.agency?.id) {
      await fetchAgencyClinics(user.agency.id, { force: true });
    }
  };

  useEffect(() => {
    if (user?.agency?.id) {
      loadClinics();
    }
  }, [user?.agency?.id]);

  // Rules of Hooks: permission check must come after all hook calls
  // The same list the tab strip renders, not the raw slug array: holding an
  // EMR-only section's slug at an agency without the EMR product is not access
  // to anything, and letting it through here opened a page with no tabs.
  if (visibleSections.length === 0) {
    return (
      <div>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex gap-2 items-center'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don't have permission to access Admin features. Please contact
            your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <Tabs
      value={activeSection}
      onValueChange={handleSectionChange}
      orientation='vertical'
      className='flex w-full flex-col gap-4 md:h-full md:flex-row md:items-stretch md:gap-6'
    >
      {/* A phone gets the native picker, which always shows the open section. */}
      <div className='relative md:hidden'>
        <select
          aria-label='Admin section'
          value={activeSection}
          onChange={e => handleSectionChange(e.target.value)}
          className='h-10 w-full appearance-none rounded-md border border-input bg-background pl-3 pr-9 text-sm'
        >
          {navEntries.map(entry => (
            <optgroup key={entry.id} label={entry.label}>
              {entry.sections.map(section => (
                <option key={section.id} value={section.id}>
                  {sectionOptionLabel(section, enrollmentCount)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <ChevronDown className='pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50' />
      </div>

      {/* The Messages launcher sits bottom-right, so the list may run into the
          bottom padding reserved for it rather than stopping short. */}
      <div className='hidden w-full space-y-4 md:block md:max-h-[calc(100%_+_var(--layout-main-pb))] md:w-48 md:shrink-0 md:self-start md:overflow-y-auto md:pb-3'>
        <div className='relative'>
          <Search className='pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
          <Input
            inputSize='sm'
            placeholder='Search settings'
            aria-label='Search settings'
            value={navQuery}
            onChange={e => setNavQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && navResults[0])
                handleSectionChange(navResults[0].id);
              if (e.key === 'Escape') setNavQuery('');
            }}
            className='pl-8'
          />
        </div>
        {query ? (
          <ul className='space-y-0.5'>
            {navResults.length === 0 ? (
              <li className='px-3 py-1.5 text-sm text-muted-foreground'>
                No settings match
              </li>
            ) : (
              navResults.map(section => (
                <li key={section.id}>
                  <button
                    type='button'
                    onClick={() => handleSectionChange(section.id)}
                    className='w-full rounded-md px-3 py-1.5 text-left text-sm hover:bg-muted'
                  >
                    {section.label}
                    <span className='block text-xs text-muted-foreground'>
                      in {ADMIN_SECTION_GROUP_LABELS[section.group]}
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        ) : (
          <TabsList className='h-auto w-full justify-start gap-1 overflow-x-auto rounded-none bg-transparent p-0 md:flex-col md:items-stretch md:gap-0 md:overflow-visible'>
            {navEntries.map(entry => (
              <React.Fragment key={entry.id}>
                <div className='px-3 pb-0.5 pt-3 text-sm font-medium text-foreground first:pt-0'>
                  {entry.label}
                </div>
                {entry.sections.map(section => (
                  <SectionTrigger
                    key={section.id}
                    section={section}
                    enrollmentCount={enrollmentCount}
                  />
                ))}
              </React.Fragment>
            ))}
          </TabsList>
        )}
      </div>

      {/* The page stays put; this pane scrolls when a section runs long. Like the
          tab column it runs into <main>'s bottom padding, and pads its own end. */}
      <div className='min-w-0 flex-1 md:h-[calc(100%_+_var(--layout-main-pb))] md:self-start md:overflow-y-auto md:border-l md:border-border/60 md:pb-[var(--layout-main-pb)] md:pl-6'>
        <TabsContent value='clinic-info' className='mt-0 space-y-6'>
          <ClinicInfoSection
            clinics={clinics}
            isLoadingClinics={isLoadingClinics}
            isSuperUser={isSuperUser}
            isRogerEmrEnabled={isRogerEmrEnabled}
            isKinnserEnabled={isKinnserEnabled}
            onReloadClinics={loadClinicsAndSyncStore}
            requestedEditClinicId={requestedEditClinicId}
            requestedFieldId={requestedClinicField}
            onEditClinicRequestHandled={clearEditClinicRequest}
            onSyncComplete={() => {
              setPhysiciansRefreshToken(token => token + 1);
              setInternalReferralSourcesRefreshToken(token => token + 1);
              setInsurancesRefreshToken(token => token + 1);
            }}
          />
        </TabsContent>

        {/* Full height so only the payer table scrolls: EnrollmentsTab and the
            PayerEnrollmentsTable wrapper must keep passing that height down. */}
        <TabsContent value='insurance' className='mt-0 space-y-6 lg:h-full'>
          <InsuranceSection
            clinics={clinics}
            isSuperUser={isSuperUser}
            isRogerEmrEnabled={isRogerEmrEnabled}
            refreshToken={insurancesRefreshToken}
            onEnrollmentCountChange={setEnrollmentCount}
            requestedEditInsuranceId={requestedEditInsuranceId}
            onEditInsuranceRequestHandled={clearEditInsuranceRequest}
          />
        </TabsContent>

        <TabsContent value='physicians' className='mt-0 space-y-6'>
          <PhysiciansSection
            clinics={clinics}
            isSuperUser={isSuperUser}
            isRogerEmrEnabled={isRogerEmrEnabled}
            refreshToken={physiciansRefreshToken}
          />
        </TabsContent>

        <TabsContent value='referral-sources' className='mt-0 space-y-6'>
          <InternalReferralSourcesSection
            clinics={clinics}
            refreshToken={internalReferralSourcesRefreshToken}
          />
        </TabsContent>

        <TabsContent value='agency-print-name' className='mt-0 space-y-6'>
          <AgencyPrintNameSection />
        </TabsContent>

        <TabsContent value='fax-number-porting' className='mt-0 space-y-6'>
          <FaxNumberPortingSection />
        </TabsContent>

        <TabsContent value='onboarding' className='mt-0 space-y-6'>
          <OnboardingDatesSection />
          <ExternalEmrSection />
        </TabsContent>

        <TabsContent value='chart-workflow' className='mt-0 space-y-6'>
          <ChartWorkflowSection />
        </TabsContent>

        <TabsContent value='hhrg-costs' className='mt-0 space-y-6'>
          <HhrgCostsSection clinics={clinics} />
        </TabsContent>

        <TabsContent value='scheduling-points' className='mt-0 space-y-6'>
          <SchedulingPointsSection
            clinics={clinics}
            isLoadingClinics={isLoadingClinics}
          />
        </TabsContent>

        {/* Staff license types. Not gated on isRogerEmrEnabled: staff
            credential compliance is not an EMR-only concern, and User Manager
            is shared with intake-only agencies. */}
        <TabsContent value='user-license-types' className='mt-0 space-y-6'>
          <UserLicenseTypesSection />
        </TabsContent>

        <TabsContent value='patient-tags' className='mt-0 space-y-6'>
          <PatientTagsAdminSection clinics={clinics} />
        </TabsContent>

        <TabsContent value='task-types' className='mt-0 space-y-6'>
          <VisitTypesSection />
        </TabsContent>

        <TabsContent value='payroll' className='mt-0 space-y-6'>
          <PayrollSettingsSection />
        </TabsContent>

        <TabsContent value='form-templates' className='mt-0 space-y-6'>
          <FormTemplatesSection />
        </TabsContent>

        <TabsContent
          value='physician-order-templates'
          className='mt-0 space-y-6'
        >
          <FormTemplatesSection target='physician_order' />
        </TabsContent>

        {/* Full height so the export list and summary scroll on their own. */}
        <TabsContent value='cahps' className='mt-0 md:h-full'>
          <CahpsSection />
        </TabsContent>
      </div>
    </Tabs>
  );
};

/** The count a section's nav entry shows, or null for none. */
const needsActionCount = (
  section: AdminSectionDefinition,
  enrollmentCount: number | null
) =>
  section.id === 'insurance' && enrollmentCount != null && enrollmentCount > 0
    ? enrollmentCount
    : null;

const sectionOptionLabel = (
  section: AdminSectionDefinition,
  enrollmentCount: number | null
) => {
  const count = needsActionCount(section, enrollmentCount);
  return count ? `${section.label} (${count} need action)` : section.label;
};

/** One section's tab in the nav, indented under its group heading. */
const SectionTrigger: React.FC<{
  section: AdminSectionDefinition;
  enrollmentCount: number | null;
}> = ({ section, enrollmentCount }) => {
  const count = needsActionCount(section, enrollmentCount);
  return (
    <TabsTrigger
      value={section.id}
      className='shrink-0 justify-start whitespace-nowrap rounded-md bg-transparent px-3 py-1.5 text-sm font-normal text-muted-foreground shadow-none transition-colors hover:bg-muted hover:text-foreground data-[state=active]:bg-primary/10 data-[state=active]:font-medium data-[state=active]:text-primary data-[state=active]:shadow-none md:ml-3'
    >
      {section.label}
      {count && (
        <Badge
          variant='secondary'
          className='ml-1.5 inline-flex h-4 min-w-4 items-center justify-center px-1.5 py-0 text-[10px]'
        >
          {count}
          <span className='sr-only'> needing action</span>
        </Badge>
      )}
    </TabsTrigger>
  );
};

export default Admin;
